import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { Box, Button, Grid } from '@mui/material';
import { Field, Form } from 'react-final-form';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BasicLayout from '../../../common/layouts/basicLayout';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { selectUnidadeSelecionada } from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import MultiSelectClientes from '../planilhas/components/MultiSelectClientes';
import MultiSelectExportador from './components/MultiSelectExportador';
import MultiSelectProcesso from './components/MultiSelectProcesso';
import { generateSpreadsheetProformaPackingListSaida } from './proformaPackingListSaida.helpers';

const ProformaPackingListSaidaPage = () => {
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  const selectedUnit = useSelector(selectUnidadeSelecionada);

  const [datas, setDatas] = useState({ dataInicio: '', dataFim: '' });

  useUnidadeNegocioGuard();

  const handleChangeDataInicio = (isoDate: string) => {
    setDatas((prev) => ({ ...prev, dataInicio: isoDate.split('T')[0] }));
  };

  const handleChangeDataFim = (isoDate: string) => {
    setDatas((prev) => ({ ...prev, dataFim: isoDate.split('T')[0] }));
  };

  const handleSubmit = useCallback(
    async (values: any) => {
      keycloak.updateToken(300);

      if (!token || !selectedUnit) {
        dispatch(setErrorFeedback({ message: 'Selecione uma unidade de negócio.' }));
        return;
      }

      if (!datas.dataInicio || !datas.dataFim) {
        dispatch(setErrorFeedback({ message: 'Informe as datas de abertura (início e fim).' }));
        return;
      }

      if (new Date(datas.dataInicio) > new Date(datas.dataFim)) {
        dispatch(setErrorFeedback({ message: 'A data de abertura inicial deve ser anterior ou igual à final.' }));
        return;
      }

      const clientesIds: number[] = (values.clientes ?? []).map((item: any) => Number(item.id));
      const exportadoresIds: number[] = (values.exportadores ?? []).map((item: any) => Number(item.id));

      const processosNumeros: string[] = (values.processos ?? []).map((item: any) => String(item.id));

      try {
        const error = await generateSpreadsheetProformaPackingListSaida(
          token,
          selectedUnit,
          clientesIds,
          exportadoresIds,
          processosNumeros,
          datas.dataInicio || undefined,
          datas.dataFim || undefined
        );
        if (error != null) {
          dispatch(setErrorFeedback({ message: error }));
        } else {
          dispatch(setSuccessFeedback({ message: 'Planilha gerada com sucesso!' }));
        }
      } catch (err) {
        dispatch(setErrorFeedback({ message: 'Erro inesperado ao gerar o relatório.' }));
      }

      keycloak.updateToken(300);
    },
    [datas, token, selectedUnit, dispatch, keycloak]
  );

  return (
    <BasicLayout title={'Proforma / Packing List Saída'} icon={<ReceiptIcon color={'secondary'} />}>
      <Box padding={3}>
        <Form onSubmit={() => {}}>
          {({ values }) => (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MultiSelectClientes name="clientes" label="Cliente" />
                </Grid>
                <Grid item xs={12}>
                  <MultiSelectExportador name="exportadores" label="Exportador" />
                </Grid>
                <Grid item xs={12}>
                  <MultiSelectProcesso name="processos" label="Processo (Follow-up)" />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    name="dataInicio"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="data-abertura-inicio"
                        name={input.name}
                        label="Data de abertura (início)"
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        required
                        onChange={(date: any) => {
                          handleChangeDataInicio(date.toISOString());
                          input.onChange(date);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    name="dataFim"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="data-abertura-fim"
                        name={input.name}
                        label="Data de abertura (fim)"
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        required
                        onChange={(date: any) => {
                          handleChangeDataFim(date.toISOString());
                          input.onChange(date);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleSubmit(values)}
                    disabled={!selectedUnit}
                  >
                    Gerar Relatório
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      </Box>
    </BasicLayout>
  );
};

export default ProformaPackingListSaidaPage;
